/**
 * v3.23 朋友圈通知：好友集合共享 + 实时新好友动态检测。
 *
 * 好友集合口径（与 FriendCirclePage 一致）：联系人 did ∪ 链上关注 did ∪ 自己。
 * 新好友动态：集合内作者（非自己）发布、scope 非 private、时间晚于朋友圈已读时间戳的帖子。
 * 触发时机：FeedService.subscribe 实时回调（session 启动后全局有效）。
 */
import type { Contact } from '@shared/types';
import { getNotifySettings, notifyCircle } from '@/lib/notify';
import { useNoticeStore } from '@/store/notices';
import { getCircleSeen } from './circleSeen';

export interface CirclePostLike {
  authorDid?: string;
  authorName?: string;
  ts: number;
  scope?: 'public' | 'friends' | 'private';
  text?: string;
}

export function buildFriendDids(
  contacts: Contact[],
  following: string[],
  selfDid: string | null,
): Set<string> {
  const set = new Set<string>();
  for (const c of contacts) if (c.did) set.add(c.did.toLowerCase());
  for (const d of following) set.add(d.toLowerCase());
  if (selfDid) set.add(selfDid.toLowerCase());
  return set;
}

function postPreview(p: CirclePostLike): string {
  const t = (p.text ?? '').trim();
  return t.length > 60 ? `${t.slice(0, 60)}…` : t || '发布了新动态';
}

/** 实时检测好友新动态 → 系统通知 + 通知中心（受全局设置控制）。 */
export function checkCircleNotices(posts: CirclePostLike[]): void {
  try {
    const settings = getNotifySettings();
    if (!settings.circle && !settings.system && !settings.sound) return;
    const seen = getCircleSeen();
    const fresh = posts.filter((p) => {
      if (!p.authorDid || p.scope === 'private') return false;
      return p.ts > seen;
    });
    if (fresh.length === 0) return;
    const first = fresh[0];
    if (settings.circle) {
      notifyCircle(first.authorName || '好友', postPreview(first));
    }
    const n = fresh.length;
    useNoticeStore.getState().addNotice(
      'circle',
      '📸 好友动态',
      n === 1
        ? `${first.authorName || '好友'}：${postPreview(first)}`
        : `${n} 位好友发布了新动态（最新：${first.authorName || '好友'}）`,
    );
  } catch {
    /* 通知异常不影响消息流 */
  }
}
